import { faBars } from '@fortawesome/pro-regular-svg-icons/faBars';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import {
  CH_BUYING_FEATURE,
  CH_HOME_FEATURE,
  CH_HOME_LINK,
  CH_LOGIN_FEATURE,
  CH_RENTING_FEATURE,
  CH_SELLING_FEATURE,
  CH_STUDENTS_FEATURE,
  NEWS_FEATURE,
  PARTIAL_GUIDES_FEATURE,
} from '@propertypal/shared/src/constants/content-hub';
import PPLogo from '@propertypal/shared/src/resources/logos/PropertyPalLogo.svg?url';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { ContentHubArticleCategory, ContentHubSubCategory } from '@propertypal/shared/src/types/strapi/overrides';
import ContentHubNewsLetter from '@propertypal/web-ui/src/content-hub/ContentHubNewsLetter';
import ContentHubNewsLetterModal from '@propertypal/web-ui/src/content-hub/ContentHubNewsLetterModal';
import ContentHubSearchResults from '@propertypal/web-ui/src/content-hub/ContentHubSearchResults';
import useSearchQuery, { SEARCH_QUERY_PARAM } from '@propertypal/web-ui/src/hooks/useSearchQuery';
import Chevron from '@propertypal/web-ui/src/icons/Chevron';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import ProfileDropdown from '@propertypal/web-ui/src/navigation/ProfileDropdown';
import { Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import togglePageScroll from '../../utils/togglePageScroll';
import SocialLinks from '../footer/social-links/SocialLinks';
import {
  Container,
  CopyrightBox,
  Footer,
  FooterContent,
  FooterLinks,
  Header,
  HeaderContent,
  HeaderPanel,
  HomeLink,
  LinksRow,
  Logo,
  Menu,
  MenuButton,
  MenuLink,
  MenuLinks,
  NavBox,
  NavSubBox,
  NavSubLink,
  ProfileContainer,
  SearchInput,
  SubMenu,
  SubMenuLink,
  SubscribeBtn,
} from './ContentHubLayout.style';

interface Props {
  subCategories: ContentHubSubCategory[];
  children?: React.ReactNode;
}

interface Category {
  label: string;
  slug: ContentHubArticleCategory;
  links: Array<{
    label: string;
    slug: string | null;
    url: string;
  }>;
  hidden?: boolean;
}

const groupSubCategories = (subCategories: ContentHubSubCategory[]) => {
  const categories: Category[] = [
    { label: 'Buying', slug: 'buying', links: [], hidden: !CH_BUYING_FEATURE },
    { label: 'Selling', slug: 'selling', links: [], hidden: !CH_SELLING_FEATURE },
    { label: 'Renting', slug: 'renting', links: [], hidden: !CH_RENTING_FEATURE },
    { label: 'Students', slug: 'students', links: [], hidden: !CH_STUDENTS_FEATURE },
  ];

  subCategories.forEach((subCategory) => {
    const category = categories.find((c) => c.slug === subCategory.attributes.articleCategory);

    if (!category?.hidden) {
      if (category && subCategory.attributes.articleCategory && subCategory.attributes.subCategory) {
        category.links.push({
          label: subCategory.attributes.subCategory,
          slug: subCategory.attributes.slug,
          url: `/${category.slug}/${subCategory.attributes.slug}`,
        });
      }
    }
  });

  return categories.filter((category) => !category.hidden);
};

const ContentHubLayout: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const links = groupSubCategories(props.subCategories);
  const searchParams = useSearchParams();
  const { searchText, setSearchText } = useSearchQuery();
  // check whether there is a faq search query
  const searchQuery = searchParams.get(SEARCH_QUERY_PARAM);

  const [activeLink, setActiveLink] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [subscribeModal, setSubscribeModal] = useState<boolean>(false);
  const [location, setLocation] = useState<string>('Top Nav');

  const page = useMemo(() => {
    if (router.query?.q) return 'Content Hub Search Page';
    if (router.query?.article) return 'Article Detail';
    if (router.query?.subCategory) return 'Content Hub Sub Category Page';
    return 'Content Hub Landing Page';
  }, [router.query]);

  const headerRef = useRef<HTMLDivElement | null>(null);

  const toggleMoreMenu = () => {
    setMenuOpen(!menuOpen);
    togglePageScroll('#contentHubMenu', menuOpen);
  };

  const updateProfileOpen = (open: boolean) => {
    setActiveLink('');
    setProfileOpen(open);
  };

  const updateActiveUpdate = (link: string) => {
    setProfileOpen(false);
    setActiveLink(link);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // @ts-ignore
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setActiveLink('');
        setProfileOpen(false);
      }
    };

    const handleRouteChange = () => {
      // close menu on route.
      setMenuOpen(false);
      togglePageScroll('#contentHubMenu', true);
    };

    const handleRouteChangeComplate = (url: string) => {
      if (typeof window !== 'undefined') {
        const { searchParams: params } = new URL(url, window.location.origin);
        if (!params.get(SEARCH_QUERY_PARAM)) setSearchText('');
      }
    };

    const handleScroll = () => {
      setLocation(`${window.scrollY > 70 ? 'Sticky' : 'Top'} Nav`);
    };

    document.addEventListener('click', handleClick);
    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('routeChangeComplete', handleRouteChangeComplate);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleClick);
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('routeChangeComplete', handleRouteChangeComplate);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Container>
        <Header data-testid="content-hub-layout">
          <HeaderContent ref={headerRef} $menuOpen={menuOpen}>
            {!menuOpen && (
              <HeaderPanel>
                <Link href="/" prefetch={false}>
                  <Text fontSize={10}>
                    <Chevron direction="left" size={8} /> Back to
                  </Text>
                  <Logo src={PPLogo.src} />
                </Link>

                <LinksRow>
                  {CH_HOME_FEATURE && (
                    <NavBox href={CH_HOME_LINK} prefetch={false} $current={router?.pathname === CH_HOME_LINK}>
                      Home
                    </NavBox>
                  )}

                  {PARTIAL_GUIDES_FEATURE &&
                    links.map((link) => (
                      <NavBox
                        as="button"
                        href="/"
                        $active={activeLink === link.label}
                        $current={router.query.category === link.slug}
                        key={link.label}
                        onClick={() => updateActiveUpdate(activeLink === link.label ? '' : link.label)}
                      >
                        {link.label}

                        <NavSubBox style={{ display: activeLink === link.label ? 'block' : 'none' }}>
                          {link.links.map((subLink) => (
                            <NavSubLink
                              href={subLink.url}
                              key={subLink.label}
                              $current={router.query.subCategory === subLink.slug}
                              prefetch={false}
                              data-testid={`content-hub-nav-sub-link${activeLink !== link.label ? '-hidden' : ''}`}
                            >
                              {subLink.label}
                            </NavSubLink>
                          ))}
                        </NavSubBox>
                      </NavBox>
                    ))}

                  {NEWS_FEATURE && (
                    <NavBox
                      href="/news-and-analysis"
                      prefetch={false}
                      $current={router.query.category === 'news-and-analysis'}
                    >
                      News &amp; Analysis
                    </NavBox>
                  )}
                </LinksRow>
              </HeaderPanel>
            )}

            <HeaderPanel>
              {!menuOpen && (
                <SubscribeBtn
                  maxWidth="248px"
                  onClick={() => {
                    trackGaEvent('subscribe_select', {
                      page,
                      location,
                    });
                    setSubscribeModal(true);
                  }}
                  testID="subscribe-to-newsletter"
                >
                  Subscribe<span> to our newsletter</span>
                </SubscribeBtn>
              )}
              {subscribeModal && (
                <ContentHubNewsLetterModal
                  show={subscribeModal}
                  onClose={() => setSubscribeModal(false)}
                  email=""
                  location={location}
                />
              )}
              <SearchInput menuOpen={menuOpen}>
                <FontAwesomeIcon icon={faSearch} color={theme.primary} />

                <input
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  aria-label="Content Hub Search"
                  placeholder="Search..."
                  data-testid="content-hub-search-input"
                />
              </SearchInput>

              {CH_LOGIN_FEATURE && (
                <ProfileContainer data-testid="profile-container">
                  <ProfileDropdown open={profileOpen} setOpen={updateProfileOpen} textColor={theme.textDark} />
                </ProfileContainer>
              )}

              <MenuButton onClick={toggleMoreMenu}>
                {menuOpen ? (
                  <>
                    <FontAwesomeIcon icon={faTimes} color={theme.textDark} size={20} />
                    <span>Close</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBars} color={theme.textDark} size={20} />
                    <span>Menu</span>
                  </>
                )}
              </MenuButton>
            </HeaderPanel>
          </HeaderContent>
        </Header>

        <Menu id="contentHubMenu" data-testid="content-hub-menu" style={{ display: menuOpen ? 'flex' : 'none' }}>
          <MenuLinks>
            {CH_HOME_FEATURE && (
              <MenuLink href={CH_HOME_LINK} $current={router?.pathname === CH_HOME_LINK}>
                Home
              </MenuLink>
            )}

            {PARTIAL_GUIDES_FEATURE &&
              links.map((link) => (
                <React.Fragment key={link.label}>
                  <MenuLink href="/" as="div" $current={router?.query?.category === link.slug}>
                    {link.label}
                  </MenuLink>
                  <SubMenu>
                    {link.links.map((subLink) => (
                      <SubMenuLink
                        href={subLink.url}
                        key={subLink.label}
                        prefetch={false}
                        $current={router?.query?.subCategory === subLink.slug}
                      >
                        <span>{subLink.label}</span>

                        <FontAwesomeIcon icon={faChevronRight} size={20} />
                      </SubMenuLink>
                    ))}
                  </SubMenu>
                </React.Fragment>
              ))}

            {NEWS_FEATURE && (
              <MenuLink href="/news-and-analysis" $current={router?.query?.category === 'news-and-analysis'}>
                News &amp; Analysis
              </MenuLink>
            )}

            <ContentHubNewsLetter nav location="Mobile Menu" page={page} onSubmit={toggleMoreMenu} />
          </MenuLinks>

          <Link
            href="/"
            style={{ padding: 15, display: 'block', borderTop: `1px solid ${theme.textLight}` }}
            prefetch={false}
          >
            <Text fontSize={10} color={theme.white}>
              <Chevron direction="left" size={8} /> Back to
            </Text>

            <Logo src={PPLogo.src} />
          </Link>
        </Menu>

        {/* if search query active, render search markup */}
        {searchQuery ? <ContentHubSearchResults searchQuery={searchQuery} homeLink={CH_HOME_LINK} /> : props.children}
      </Container>
      <Footer>
        <FooterContent>
          <ContentHubNewsLetter location="Footer" page={page} />

          <FooterLinks>
            {PARTIAL_GUIDES_FEATURE && (
              <div>
                <h6>GUIDES</h6>

                {CH_BUYING_FEATURE && (
                  <p>
                    <Link href="/buying" prefetch={false}>
                      Buying
                    </Link>
                  </p>
                )}
                {CH_SELLING_FEATURE && (
                  <p>
                    <Link href="/selling" prefetch={false}>
                      Selling
                    </Link>
                  </p>
                )}
                {CH_RENTING_FEATURE && (
                  <p>
                    <Link href="/renting" prefetch={false}>
                      Renting
                    </Link>
                  </p>
                )}
                {CH_STUDENTS_FEATURE && (
                  <p>
                    <Link href="/student" prefetch={false}>
                      Students
                    </Link>
                  </p>
                )}
              </div>
            )}

            <div>
              <h6>QUICK LINKS</h6>

              {NEWS_FEATURE && (
                <p>
                  <Link href="/news-and-analysis" prefetch={false}>
                    News &amp; Analysis
                  </Link>
                </p>
              )}
              <p>
                <Link href="/faq" prefetch={false}>
                  FAQ
                </Link>
              </p>
              <p>
                <Link href="/search" prefetch={false}>
                  Find a Property
                </Link>
              </p>
              <p>
                <Link href="/contact" prefetch={false}>
                  Contact Us
                </Link>
              </p>
            </div>

            <div>
              <h6>FIND US ON SOCIAL</h6>

              <SocialLinks />

              <HomeLink href="/" prefetch={false}>
                <span>
                  <Chevron size={8} direction="left" /> Back to
                </span>

                <img src={PPLogo.src} alt="PropertyPal" />
              </HomeLink>
            </div>
          </FooterLinks>
        </FooterContent>

        <CopyrightBox>
          <p>
            © Copyright PropertyPal.com 2007-{new Date().getFullYear()} |{' '}
            <Link href="/privacy" prefetch={false}>
              Privacy Policy
            </Link>{' '}
            |{' '}
            <Link href="/cookie-policy" prefetch={false}>
              Cookie Policy
            </Link>{' '}
            |{' '}
            <Link href="/terms" prefetch={false}>
              Terms & Conditions
            </Link>
          </p>
        </CopyrightBox>
      </Footer>
    </>
  );
};

export default ContentHubLayout;
